package com.banyan.flightviewer.web.components

import com.banyan.flightviewer.platform.SettingsStore
import com.banyan.flightviewer.state.AircraftData
import com.banyan.flightviewer.state.AppSettings
import com.banyan.flightviewer.web.components.aircraft.SortColumn
import com.banyan.flightviewer.web.components.aircraft.SortDirection
import com.banyan.flightviewer.web.components.aircraft.sortAircraft
import com.banyan.flightviewer.web.components.util.altitudeUnitLabel
import com.banyan.flightviewer.web.components.util.distanceUnitLabel
import com.banyan.flightviewer.web.components.util.formatAltitudeShort
import com.banyan.flightviewer.web.components.util.formatDistance
import com.banyan.flightviewer.web.components.util.formatSpeedShort
import com.banyan.flightviewer.web.components.util.formatVerticalRateShort
import com.banyan.flightviewer.web.components.util.speedUnitLabel
import com.banyan.flightviewer.web.components.util.vrClass
import com.banyan.flightviewer.web.dom.bindFlow
import com.banyan.flightviewer.web.dom.div
import com.banyan.flightviewer.web.dom.onClick
import com.banyan.flightviewer.web.dom.removeAllChildren
import com.banyan.flightviewer.web.dom.table
import com.banyan.flightviewer.web.dom.tbody
import com.banyan.flightviewer.web.dom.td
import com.banyan.flightviewer.web.dom.th
import com.banyan.flightviewer.web.dom.thead
import com.banyan.flightviewer.web.dom.tr
import com.banyan.flightviewer.web.state.WebAppContainer
import kotlinx.browser.document
import kotlinx.browser.window
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.asStateFlow
import org.w3c.dom.HTMLDivElement
import org.w3c.dom.HTMLInputElement
import org.w3c.dom.HTMLLabelElement
import org.w3c.dom.HTMLTableSectionElement
import org.w3c.dom.Node
import org.w3c.dom.events.Event
import org.w3c.dom.events.MouseEvent

/**
 * Column definition. `label` and `value` both receive the current unit
 * preferences so each column can render a units-aware header and body.
 */
private data class Column(
    val id: String,
    val sortKey: SortColumn?,     // null = column isn't sortable (none here, but keep the escape hatch)
    val mandatory: Boolean,
    val defaultVisible: Boolean,
    val label: (AppSettings) -> String,
    val value: (AircraftData, AppSettings) -> String,
    val valueClass: (AircraftData) -> String = { "" },
)

private val COLUMNS = listOf(
    Column(
        id = "flight", sortKey = SortColumn.CALLSIGN,
        mandatory = true, defaultVisible = true,
        label = { "Flight" },
        value = { ac, _ -> ac.callsign },
    ),
    Column(
        id = "route", sortKey = null,
        mandatory = false, defaultVisible = true,
        label = { "From \u2192 To" },
        value = { ac, _ -> routeOf(ac) },
    ),
    Column(
        id = "type", sortKey = SortColumn.TYPE,
        mandatory = false, defaultVisible = true,
        label = { "Type" },
        value = { ac, _ -> ac.aircraftType.takeIf { it.isNotBlank() && it != "Unknown" } ?: "" },
    ),
    Column(
        id = "alt", sortKey = SortColumn.ALTITUDE,
        mandatory = false, defaultVisible = true,
        label = { s -> "Alt (${altitudeUnitLabel(s.altitudeUnits)})" },
        value = { ac, s -> formatAltitudeShort(ac.altitude, s.altitudeUnits) },
    ),
    Column(
        id = "spd", sortKey = SortColumn.SPEED,
        mandatory = false, defaultVisible = true,
        label = { s -> "Spd (${speedUnitLabel(s.speedUnits)})" },
        value = { ac, s -> formatSpeedShort(ac.speed, s.speedUnits) },
    ),
    Column(
        id = "hdg", sortKey = SortColumn.HEADING,
        mandatory = false, defaultVisible = false,
        label = { "Hdg" },
        value = { ac, _ -> "${ac.heading.toInt()}\u00B0" },
    ),
    Column(
        id = "vs", sortKey = SortColumn.VERT_RATE,
        mandatory = false, defaultVisible = false,
        label = { "V/S" },
        value = { ac, _ -> formatVerticalRateShort(ac.verticalRate) },
        valueClass = { ac -> vrClass(ac.verticalRate) },
    ),
    Column(
        id = "dist", sortKey = SortColumn.DISTANCE,
        mandatory = false, defaultVisible = true,
        label = { s -> "Dist (${distanceUnitLabel(s.distanceUnits)})" },
        value = { ac, s -> formatDistance(ac.distance, s.distanceUnits) },
    ),
)

/** Tracks which columns are visible. Backed by SettingsStore (localStorage). */
private class ColumnVisibilityState(private val store: SettingsStore) {

    private val _visible = MutableStateFlow(loadInitial())
    val visible = _visible.asStateFlow()

    private fun loadInitial(): Set<String> = COLUMNS
        .filter { col -> store.getBoolean(storeKey(col.id)) ?: col.defaultVisible }
        .map { it.id }
        .toSet()

    fun isVisible(id: String): Boolean = id in _visible.value

    fun toggle(id: String) {
        val nowVisible = id !in _visible.value
        store.putBoolean(storeKey(id), nowVisible)
        store.flush()
        _visible.value = if (nowVisible) _visible.value + id else _visible.value - id
    }

    private fun storeKey(id: String) = "col_$id"
}

fun mountAircraftTable(parent: org.w3c.dom.Element, app: WebAppContainer) {
    val columnState = ColumnVisibilityState(app.settingsStore)

    parent.div(className = "fv-table-wrap") {
        val emptyState = div(className = "fv-table-wrap__empty") {
            textContent = "Waiting for aircraft\u2026"
        }

        val tableScroll = div(className = "fv-table-scroll")
        var tbodyEl: HTMLTableSectionElement? = null
        var headerTr: org.w3c.dom.HTMLTableRowElement? = null

        tableScroll.table(className = "fv-table") {
            thead {
                headerTr = tr { }
                addEventListener("contextmenu", { ev ->
                    ev.preventDefault()
                    val mouseEv = ev as MouseEvent
                    showColumnMenu(mouseEv.clientX.toDouble(), mouseEv.clientY.toDouble(), columnState, app.appState.settings.value)
                })
            }
            tbodyEl = tbody { }
        }

        tableScroll.asDynamic().style.display = "none"

        // Render/refresh the header row. Called whenever settings (units) or
        // sort state change. Clicking a sortable <th> calls uiState.toggleSort.
        fun renderHeader() {
            val head = headerTr ?: return
            head.removeAllChildren()
            val settings = app.appState.settings.value
            val sortCol = app.uiState.sortColumn.value
            val sortDir = app.uiState.sortDirection.value
            val arrow = if (sortDir == SortDirection.ASC) " \u25B2" else " \u25BC"
            COLUMNS.forEach { col ->
                val base = col.label(settings)
                val isSorted = col.sortKey != null && col.sortKey == sortCol
                val text = if (isSorted) "$base$arrow" else base
                val cls = buildString {
                    append("fv-col-")
                    append(col.id)
                    if (isSorted) append(" fv-col-sorted")
                    if (col.sortKey != null) append(" fv-col-sortable")
                }
                val th = head.th(className = cls, text = text)
                if (col.sortKey != null) {
                    th.onClick {
                        app.uiState.toggleSort(col.sortKey!!)
                    }
                }
            }
        }

        // Apply current visibility set to every header + cell.
        fun applyVisibility() {
            val visibleSet = columnState.visible.value
            COLUMNS.forEach { col ->
                val cells = tableScroll.querySelectorAll(".fv-col-${col.id}")
                val hide = col.id !in visibleSet
                for (i in 0 until cells.length) {
                    val el = cells.item(i) as? org.w3c.dom.Element ?: continue
                    if (hide) el.classList.add("fv-col-hidden")
                    else el.classList.remove("fv-col-hidden")
                }
            }
        }

        fun rowClass(ac: AircraftData, selectedHex: String?): String {
            val parts = mutableListOf<String>()
            if (ac.hex == selectedHex) parts.add("selected")
            when (ac.squawk) {
                "7700" -> parts.add("fv-table__row--emer-7700")
                "7600" -> parts.add("fv-table__row--emer-7600")
                "7500" -> parts.add("fv-table__row--emer-7500")
            }
            return parts.joinToString(" ")
        }

        fun renderRows() {
            val body = tbodyEl ?: return
            val list = app.uiState.filteredAircraftList.value
            if (list.isEmpty()) {
                emptyState.asDynamic().style.display = "flex"
                tableScroll.asDynamic().style.display = "none"
                body.removeAllChildren()
                return
            }
            emptyState.asDynamic().style.display = "none"
            tableScroll.asDynamic().style.display = "block"

            val sorted = sortAircraft(list, app.uiState.sortColumn.value, app.uiState.sortDirection.value)
            val selected = app.appState.selectedCallsign.value
            val settings = app.appState.settings.value

            body.removeAllChildren()
            sorted.forEach { ac ->
                body.tr(className = rowClass(ac, selected)) {
                    COLUMNS.forEach { col ->
                        val extraCls = col.valueClass(ac).takeIf { it.isNotBlank() }?.let { " $it" } ?: ""
                        td(className = "fv-col-${col.id}$extraCls", text = col.value(ac, settings))
                    }
                    onClick { app.appState.selectFlight(ac.hex) }
                }
            }
            applyVisibility()
        }

        // ── Wiring ────────────────────────────────────────────────────
        renderHeader()

        app.appState.scope.bindFlow(app.uiState.filteredAircraftList) { renderRows() }
        app.appState.scope.bindFlow(app.uiState.sortColumn) { renderHeader(); renderRows() }
        app.appState.scope.bindFlow(app.uiState.sortDirection) { renderHeader(); renderRows() }
        app.appState.scope.bindFlow(app.appState.settings) {
            renderHeader()
            renderRows()     // unit labels and values follow settings
        }
        app.appState.scope.bindFlow(app.appState.selectedCallsign) { selected ->
            val body = tbodyEl ?: return@bindFlow
            val rows = body.children
            val sorted = sortAircraft(
                app.uiState.filteredAircraftList.value,
                app.uiState.sortColumn.value,
                app.uiState.sortDirection.value,
            )
            for (i in 0 until rows.length) {
                val row = rows.item(i) ?: continue
                if (i < sorted.size) {
                    row.className = rowClass(sorted[i], selected)
                }
            }
        }
        app.appState.scope.bindFlow(columnState.visible) { applyVisibility() }
    }
}

/**
 * Show a popup at viewport coords (x, y) listing every non-mandatory column
 * with a checkbox. Clicking a checkbox toggles that column's visibility;
 * the popup stays open so the user can flip several at once. Closes on
 * Escape or click outside.
 */
private fun showColumnMenu(x: Double, y: Double, state: ColumnVisibilityState, settings: AppSettings) {
    // Tear down any existing menu first
    document.querySelector(".fv-col-menu")?.parentNode?.removeChild(
        document.querySelector(".fv-col-menu")!!,
    )

    val menu = document.createElement("div") as HTMLDivElement
    menu.className = "fv-col-menu"
    menu.style.left = "${x}px"
    menu.style.top = "${y}px"

    val header = document.createElement("div")
    header.className = "fv-col-menu__header"
    header.textContent = "Columns"
    menu.appendChild(header)

    COLUMNS.filterNot { it.mandatory }.forEach { col ->
        val item = document.createElement("label") as HTMLLabelElement
        item.className = "fv-col-menu__item"

        val cb = document.createElement("input") as HTMLInputElement
        cb.type = "checkbox"
        cb.checked = state.isVisible(col.id)
        cb.addEventListener("change", { state.toggle(col.id) })
        item.appendChild(cb)

        val labelText = document.createElement("span")
        labelText.textContent = col.label(settings)
        item.appendChild(labelText)

        menu.appendChild(item)
    }

    document.body!!.appendChild(menu)

    // Re-position if the menu would overflow the right or bottom edge.
    val rect = menu.getBoundingClientRect()
    if (rect.right > window.innerWidth) {
        menu.style.left = "${(x - rect.width).coerceAtLeast(4.0)}px"
    }
    if (rect.bottom > window.innerHeight) {
        menu.style.top = "${(y - rect.height).coerceAtLeast(4.0)}px"
    }

    var closeClick: ((Event) -> Unit)? = null
    var closeKey: ((Event) -> Unit)? = null
    fun close() {
        if (menu.parentNode != null) menu.parentNode!!.removeChild(menu)
        closeClick?.let { document.removeEventListener("click", it) }
        closeKey?.let { document.removeEventListener("keydown", it) }
    }
    closeClick = { ev ->
        if (!menu.contains(ev.target as? Node)) close()
    }
    closeKey = { ev ->
        if (ev.asDynamic().key as? String == "Escape") close()
    }
    window.setTimeout({
        document.addEventListener("click", closeClick!!)
        document.addEventListener("keydown", closeKey!!)
    }, 0)
}

private fun routeOf(ac: AircraftData): String {
    val o = ac.origin.takeIf { it.isNotBlank() && it != "_none" } ?: "?"
    val d = ac.destination.takeIf { it.isNotBlank() && it != "_none" } ?: "?"
    return if (o == "?" && d == "?") "" else "$o \u2192 $d"
}
